import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { PathwayUIMessage, StoredConversation } from './types';

import { store } from '@/atoms';

export const conversationHistoryAtom = atomWithStorage<StoredConversation[]>(
  'app.smart-search.conversation-history',
  [],
  undefined,
  { getOnInit: true },
);
conversationHistoryAtom.debugLabel = 'smartSearchConversationHistory';

export const currentConversationIdAtom = atom<string | null>(null);
currentConversationIdAtom.debugLabel = 'smartSearchCurrentConversationId';

export const historyDrawerOpenAtom = atom(false);
historyDrawerOpenAtom.debugLabel = 'smartSearchHistoryDrawerOpen';

/**
 * Pending first user message for a newly-created conversation. Set by
 * `InputPrompt` when the user submits before a chat exists; consumed by the
 * chat provider once the hook mounts so the message is sent exactly once.
 */
export const pendingFirstMessageAtom = atom<string | null>(null);
pendingFirstMessageAtom.debugLabel = 'smartSearchPendingFirstMessage';

/**
 * Selected chat model `key` (matches a `ChatModelInfo.key` from `GET /chat/models`).
 * `null` means "let the server pick its default".
 */
export const modelKeyAtom = atomWithStorage<string | null>(
  'app.smart-search.model-key',
  null,
  undefined,
  { getOnInit: true },
);
modelKeyAtom.debugLabel = 'smartSearchModelKey';

/** Whether extended reasoning is enabled. */
export const extendedReasoningAtom = atomWithStorage(
  'app.smart-search.extended-reasoning',
  false,
  undefined,
  { getOnInit: true },
);
extendedReasoningAtom.debugLabel = 'smartSearchExtendedReasoning';

/** Creates or replaces a conversation in history by id. */
export const upsertConversation = (conversation: StoredConversation) => {
  const prev = store.get(conversationHistoryAtom);
  const exists = prev.some(c => c.id === conversation.id);
  const next = exists
    ? prev.map(c => (c.id === conversation.id ? conversation : c))
    : [conversation, ...prev];
  store.set(conversationHistoryAtom, next);
};

/**
 * Replaces the `messages` for a stored conversation. Bumps `updatedAt`.
 * If the conversation does not exist, does nothing.
 */
export const patchConversationMessages = (
  conversationId: string,
  messages: PathwayUIMessage[],
) => {
  const prev = store.get(conversationHistoryAtom);
  const existing = prev.find(c => c.id === conversationId);
  if (!existing) return;
  const updated: StoredConversation = {
    ...existing,
    messages,
    updatedAt: new Date().toISOString(),
  };
  const next = [updated, ...prev.filter(c => c.id !== conversationId)];
  store.set(conversationHistoryAtom, next);
};

export const deleteConversation = (conversationId: string) => {
  store.set(conversationHistoryAtom, prev =>
    prev.filter(c => c.id !== conversationId),
  );
};

export const renameConversation = (conversationId: string, title: string) => {
  store.set(conversationHistoryAtom, prev =>
    prev.map(c => (c.id === conversationId ? { ...c, title } : c)),
  );
};
